import { createBaseDevice } from './shared'

const DMP_PROXY_TYPES = [
  'alarm_bridge_dmp',
  'alarm_bridge_dmp_area',
  'alarm_bridge_dmp_device',
]

const getVirtualSlotType = (floorDevice) => {
  const id = String(floorDevice.id_generic ?? '')
  if (id.startsWith('dmp-status-')) return 'status'
  if (id.startsWith('dmp-action-')) return 'action'
  return null
}

/**
 * Some placed panel devices use the shared alarm_bridge_device proxy type.
 * Their vendor-specific type remains available in the related device payload.
 */
const isDmpFloorDevice = (floorDevice) => {
  const proxyType = floorDevice.device?.type
  const relatedType = floorDevice.device?.related_device?.type

  return DMP_PROXY_TYPES.includes(proxyType) || DMP_PROXY_TYPES.includes(relatedType)
}

const resolveDmpPanelProxyId = (baseDevice, context) => {
  if (baseDevice.panel_proxy_id) return baseDevice.panel_proxy_id

  const panelFloorDevice = context.planeDevices.find((item) => (
    item.device?.type === 'alarm_bridge_dmp'
    || item.device?.camera_type === 'alarm_bridge_dmp'
  ))

  return panelFloorDevice?.id_camera_proxy ?? null
}

export const dmpAdapter = {
  name: 'dmp',
  matches(floorDevice, context) {
    const panelProxyId = floorDevice.device?.panel_proxy_id
    const belongsToDmpPanel = panelProxyId !== null
      && panelProxyId !== undefined
      && context.relations.dmpPanelProxyIds.includes(String(panelProxyId))

    return !!getVirtualSlotType(floorDevice)
      || isDmpFloorDevice(floorDevice)
      || belongsToDmpPanel
  },
  normalize(floorDevice, context) {
    const baseDevice = createBaseDevice(floorDevice, context)
    const virtualSlotType = getVirtualSlotType(floorDevice)

    if (virtualSlotType === 'status') {
      const status = context.relations.dmpStatuses[floorDevice.id_generic] ?? {}
      return {
        ...baseDevice,
        vendor: 'dmp',
        kind: 'panel_status',
        name: status.name ?? baseDevice.name,
        device_type: 'dmp_panel_status',
        camera_type: 'dmp_panel_status',
        icon: status.icon ?? null,
        color: status.color ?? null,
        color_icon: status.color_icon ?? null,
        text_color: status.text_color ?? null,
        status: status.status ?? null,
        value: status.value ?? null,
        status_key: status.status_key ?? null,
        panel_id: status.panel_id ?? null,
        panel_proxy_id: status.panel_proxy_id ?? null,
        should_pulse: status.should_pulse === true,
        showAlarm: status.should_pulse === true,
        isCustomIcon: status.isCustomIcon ?? false,
      }
    }

    if (virtualSlotType === 'action') {
      const action = context.relations.dmpActions[floorDevice.id_generic] ?? {}
      return {
        ...baseDevice,
        vendor: 'dmp',
        kind: 'panel_action',
        name: action.name ?? baseDevice.name,
        device_type: 'dmp_panel_action',
        camera_type: 'dmp_panel_action',
        icon: action.icon ?? null,
        color: action.color ?? null,
        color_icon: action.color_icon ?? null,
        text_color: action.text_color ?? null,
        actions: action.actions ?? {},
      }
    }

    return {
      ...baseDevice,
      vendor: 'dmp',
      kind: baseDevice.panel_device_type ?? baseDevice.kind,
      panel_proxy_id: resolveDmpPanelProxyId(baseDevice, context),
    }
  },
}
